import React, { useState, useEffect, useMemo } from 'react';
import { Dish, Pack } from '../types';
import { useData } from '../context/DataContext';
import { CameraIcon, FireIcon, PlusIcon, XIcon, CheckIcon } from './icons';

interface PackFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (pack: Omit<Pack, 'id'> | Pack) => void;
  packToEdit?: Pack | null;
  restaurantDishes: Dish[];
  restaurantExtras?: any[];
  isSaving?: boolean;
}

const PackFormModal: React.FC<PackFormModalProps> = ({ isOpen, onClose, onSave, packToEdit, restaurantDishes, restaurantExtras = [], isSaving }) => {
  const { uploadImage } = useData();
  const [pack, setPack] = useState<{
    name: string,
    description: string,
    image: string,
    dishes: (Dish & { quantity: number })[],
    packPrice: number,
    available: boolean,
    extras: (Extra & { quantity: number })[]
  }>({
    name: '',
    description: '',
    image: '',
    dishes: [],
    packPrice: 0,
    available: true,
    extras: []
  });

  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (packToEdit) {
      setPack({
        name: packToEdit.name,
        description: packToEdit.description,
        image: packToEdit.image || '',
        dishes: (packToEdit.dishes || []).map(d => ({ ...d, quantity: d.quantity || 1 })),
        packPrice: packToEdit.packPrice,
        available: packToEdit.available ?? true,
        extras: (packToEdit.extras || []).map(e => ({ ...e, quantity: e.quantity || 1 }))
      });
    } else {
      setPack({
        name: '',
        description: '',
        image: '',
        dishes: [],
        packPrice: 0,
        available: true,
        extras: []
      });
    }
  }, [packToEdit, isOpen]);

  const originalTotal = useMemo(() => {
    const dishesTotal = pack.dishes.reduce((sum, d) => sum + (d.price * (d.quantity || 1)), 0);
    const extrasTotal = pack.extras?.reduce((sum, e) => sum + (e.price * (e.quantity || 1)), 0) || 0;
    return dishesTotal + extrasTotal;
  }, [pack.dishes, pack.extras]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    try {
      setIsUploading(true);
      const url = await uploadImage(file);
      setPack(prev => ({ ...prev, image: url }));
    } catch (error: any) {
      alert(error.message || "Erreur lors de l'upload.");
    } finally {
      setIsUploading(false);
    }
  };

  const toggleDishSelection = (dish: Dish) => {
    setPack(prev => {
      const exists = prev.dishes.find(d => d.id === dish.id);
      if (exists) {
        return { ...prev, dishes: prev.dishes.filter(d => d.id !== dish.id) };
      } else {
        return { ...prev, dishes: [...prev.dishes, { ...dish, quantity: 1 }] };
      }
    });
  };

  const updateDishQuantity = (dishId: string, delta: number) => {
    setPack(prev => ({
      ...prev,
      dishes: prev.dishes.map(d => 
        d.id === dishId ? { ...d, quantity: Math.max(1, (d.quantity || 1) + delta) } : d
      )
    }));
  };

  const toggleExtraSelection = (extra: any) => {
    setPack(prev => {
      const currentExtras = prev.extras || [];
      const exists = currentExtras.find((e: any) => e.id === extra.id);
      if (exists) {
        return { ...prev, extras: currentExtras.filter((e: any) => e.id !== extra.id) };
      } else {
        return { ...prev, extras: [...currentExtras, { ...extra, quantity: 1 }] };
      }
    });
  };

  const updateExtraQuantity = (extraId: string, delta: number) => {
    setPack(prev => ({
      ...prev,
      extras: prev.extras.map(e => 
        e.id === extraId ? { ...e, quantity: Math.max(1, (e.quantity || 1) + delta) } : e
      )
    }));
  };

  const groupedDishes = useMemo(() => {
    const groups: { [key: string]: Dish[] } = {};
    const list = searchQuery 
      ? restaurantDishes.filter(d => d.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : restaurantDishes;
      
    list.forEach(d => {
      const cat = d.category || 'Autres';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(d);
    });
    return groups;
  }, [restaurantDishes, searchQuery]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pack.dishes.length === 0) {
      alert("Veuillez sélectionner au moins un plat.");
      return;
    }
    const packData = {
      ...pack,
      originalPrice: originalTotal
    };
    onSave(packToEdit ? { ...packData, id: packToEdit.id } : packData);
  };

  return (
    <div className="fixed inset-0 bg-white md:bg-black/60 z-[10001] flex justify-center items-center overflow-hidden">
      <div className="bg-white rounded-none md:rounded-2xl shadow-2xl w-full h-full md:h-auto md:max-h-[90vh] md:max-w-5xl flex flex-col relative">
        
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-gray-800">{packToEdit ? 'Modifier le Pack' : 'Créer un Nouveau Pack'}</h2>
            <button
              type="button"
              onClick={() => setPack(prev => ({ ...prev, available: !prev.available }))}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all border-2 ${pack.available
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}
            >
              <div className={`w-2 h-2 rounded-full ${pack.available ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              <span className="text-xs font-black uppercase tracking-wider">
                {pack.available ? 'Disponible' : 'Indisponible'}
              </span>
            </button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto md:overflow-hidden flex flex-col md:flex-row">
          {/* Left Side: Form Details */}
          <div className="w-full md:w-1/2 p-4 md:p-8 overflow-y-visible md:overflow-y-auto border-b md:border-b-0 md:border-r border-gray-100 bg-white">
            <form id="pack-form" onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nom du Pack</label>
                <input
                  value={pack.name}
                  onChange={e => setPack(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Pack Familial"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <textarea
                  value={pack.description}
                  onChange={e => setPack(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Décrivez l'offre promotionnelle..."
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Image du Pack</label>
                <div className="relative w-full aspect-video rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors group overflow-hidden">
                  {pack.image ? (
                    <img src={pack.image} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                      <CameraIcon className="w-10 h-10 mb-2" />
                      <span className="text-sm">Ajouter une image</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={handleImageUpload}
                  />
                  {isUploading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Right Side: Dish Selector - RESTORED */}
          <div className="w-full md:w-1/2 p-4 md:p-8 flex flex-col bg-gray-50/30">
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center justify-between">
                <span>Sélectionner les plats ({pack.dishes.length} sélectionnés)</span>
                {pack.dishes.length > 0 && (
                  <button onClick={() => setPack(prev => ({ ...prev, dishes: [] }))} className="text-red-500 text-xs font-bold hover:underline">Dessélectionner tout</button>
                )}
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher un plat..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full px-10 py-2.5 rounded-full border border-gray-200 focus:border-red-500 outline-none text-sm bg-white"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-2 space-y-6">
              {Object.entries(groupedDishes).map(([category, dishes]) => (
                <div key={category}>
                   <h5 className="text-[10px] font-black uppercase text-gray-400 mb-3 tracking-widest bg-gray-100/50 py-1 px-3 rounded-full inline-block">{category}</h5>
                   <div className="space-y-2">
                     {dishes.map(dish => {
                       const isSelected = pack.dishes.some(d => d.id === dish.id);
                       return (
                         <div
                           key={dish.id}
                           className={`p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${isSelected ? 'border-red-500 bg-red-50' : 'border-white bg-white hover:border-gray-200 cursor-pointer'}`}
                           onClick={() => !isSelected && toggleDishSelection(dish)}
                         >
                           <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                             {dish.image ? <img src={dish.image} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px]">🍽️</div>}
                           </div>
                           <div className="flex-1 min-w-0">
                             <h4 className={`font-bold text-xs truncate ${isSelected ? 'text-red-900' : 'text-gray-800'}`}>{dish.name}</h4>
                             <p className="text-[10px] text-gray-400 font-bold">{dish.price} DA</p>
                           </div>
                           
                           {isSelected ? (
                             <div className="flex items-center gap-2 bg-white px-2 py-1 rounded-lg border border-red-200">
                               <button type="button" onClick={(e) => { e.stopPropagation(); updateDishQuantity(dish.id, -1); }} className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-md text-red-600 font-black">-</button>
                               <span className="text-xs font-black min-w-[20px] text-center">{pack.dishes.find(d => d.id === dish.id)?.quantity || 1}</span>
                               <button type="button" onClick={(e) => { e.stopPropagation(); updateDishQuantity(dish.id, 1); }} className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-md text-green-600 font-black">+</button>
                               <button type="button" onClick={(e) => { e.stopPropagation(); toggleDishSelection(dish); }} className="ml-1 text-gray-300 hover:text-red-500"><XIcon className="w-3.5 h-3.5" /></button>
                             </div>
                           ) : (
                             <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'bg-red-500 border-red-500' : 'border-gray-300'}`}>
                               {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                             </div>
                           )}
                         </div>
                       );
                     })}
                   </div>
                </div>
              ))}

              {restaurantExtras.length > 0 && (
                <div className="pt-4 border-t border-gray-100">
                  <h5 className="text-[10px] font-black uppercase text-red-600 mb-3 tracking-widest bg-red-50 py-1 px-3 rounded-full inline-block">Extras du Restaurant</h5>
                  <div className="grid grid-cols-2 gap-2">
                    {restaurantExtras.map(extra => {
                      const isSelected = pack.extras?.some((e: any) => e.id === extra.id);
                      return (
                        <div
                          key={extra.id}
                          className={`p-3 rounded-xl border-2 transition-all flex items-center justify-between ${isSelected ? 'border-red-600 bg-red-50' : 'border-white bg-white hover:border-gray-200 cursor-pointer'}`}
                          onClick={() => !isSelected && toggleExtraSelection(extra)}
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="text-[10px] font-bold text-gray-800 truncate">{extra.name}</span>
                            <span className="text-[10px] font-black text-red-600">{extra.price} DA</span>
                          </div>
                          
                          {isSelected ? (
                            <div className="flex items-center gap-1.5 bg-white px-1.5 py-0.5 rounded-lg border border-red-200 ml-2">
                              <button type="button" onClick={(e) => { e.stopPropagation(); updateExtraQuantity(extra.id, -1); }} className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded-md text-red-600 font-black text-[10px]">-</button>
                              <span className="text-[10px] font-black min-w-[14px] text-center">{pack.extras.find(e => e.id === extra.id)?.quantity || 1}</span>
                              <button type="button" onClick={(e) => { e.stopPropagation(); updateExtraQuantity(extra.id, 1); }} className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded-md text-green-600 font-black text-[10px]">+</button>
                              <button type="button" onClick={(e) => { e.stopPropagation(); toggleExtraSelection(extra); }} className="ml-1 text-gray-300 hover:text-red-500"><XIcon className="w-3 h-3" /></button>
                            </div>
                          ) : (
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-red-600 border-red-600' : 'border-gray-300'}`}>
                              {isSelected && <CheckIcon className="w-2.5 h-2.5 text-white" />}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {Object.keys(groupedDishes).length === 0 && (
                <div className="text-center py-20 text-gray-400 text-sm italic">Aucun plat trouvé</div>
              )}
            </div>
          </div>
        </div>

        {/* Footer with Comparative Pricing */}
        <div className="p-4 md:p-6 border-t border-gray-100 bg-gray-50 flex flex-col md:flex-row justify-between items-center gap-4 flex-shrink-0">
          
          <div className="flex-1 w-full md:w-auto bg-white p-4 rounded-2xl border border-gray-200 flex items-center gap-6 shadow-sm">
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-2 mb-2">
                <label className="text-[11px] font-black text-red-600 uppercase tracking-widest">Prix Promo Pack (DA)</label>
                {originalTotal > 0 && (
                  <div className="bg-gray-50 px-3 py-1 rounded-full flex items-center gap-1.5 border border-gray-100 shadow-sm">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">Au lieu de</span>
                    <span className="text-[11px] font-black text-red-600">
                      {originalTotal.toLocaleString()} DA
                    </span>
                  </div>
                )}
              </div>
              <input
                type="number"
                value={pack.packPrice}
                onChange={e => setPack(prev => ({ ...prev, packPrice: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2.5 rounded-xl border-2 border-green-50 focus:border-green-500 outline-none text-2xl font-black text-green-600 bg-green-50/30"
                required
              />
            </div>

            <div className="hidden lg:flex flex-col items-end border-l border-gray-100 pl-6">
              <span className="text-[10px] font-black text-green-600 uppercase tracking-widest mb-1">Économie</span>
              <span className="text-xl font-black text-green-600 tabular-nums">
                -{originalTotal > pack.packPrice ? (originalTotal - pack.packPrice).toLocaleString() : 0} DA
              </span>
            </div>
          </div>

          <div className="flex gap-3 w-full md:w-auto">
            <button type="button" onClick={onClose} className="flex-1 md:flex-none px-6 py-4 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-colors text-sm">Annuler</button>
            <button form="pack-form" type="submit" disabled={isSaving || pack.dishes.length === 0} className="flex-1 md:flex-none px-10 py-4 rounded-xl bg-red-600 text-white font-black hover:bg-red-700 shadow-xl shadow-red-100 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
              {isSaving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackFormModal;
